import classNames from 'classnames';
import {ChangeEvent, FC, useCallback, useState} from 'react';

import {Icon} from 'components/Icon/Icon';
import {Text} from 'components/Text/Text';

import styles from './Counter.module.css';

type Props = {
    className?: string;
    onChange?: (value: number) => void;
    onBlur?: () => void;
    value: number;
    minValue?: number;
    maxValue?: number;
    // по сколько за раз нужно прибавлять/отнимать
    step?: number;
    editable?: boolean;
    readonly?: boolean;
};
export const Counter: FC<Props> = ({
    className,
    value: valueFromProp,
    step = 1,
    minValue = 1,
    maxValue = 999,
    editable = true,
    readonly,
    onChange,
    onBlur,
}) => {
    const [value, setValue] = useState(valueFromProp);

    const handleAdd = useCallback(() => {
        const newValue = value + step;
        if (newValue > maxValue) {
            return;
        }

        setValue(newValue);
        onChange?.(newValue);
    }, [onChange, step, value, maxValue]);

    const handleSubstract = useCallback(() => {
        const newValue = value - step;
        if (newValue < minValue) {
            return;
        }

        setValue(newValue);
        onChange?.(newValue);
    }, [onChange, step, value, minValue]);

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const value = Number(event.target.value) || 1;

            if (value > maxValue) {
                setValue(maxValue);
            } else {
                setValue(value);
            }
        },
        [maxValue],
    );

    const handleBlur = useCallback(() => {
        const targetValue = Math.floor(value / step) * step;

        if (targetValue < minValue) {
            onChange?.(minValue);
        } else if (targetValue > maxValue) {
            onChange?.(maxValue);
        } else {
            onChange?.(targetValue);
        }
        onBlur?.();
    }, [onChange, onBlur, minValue, maxValue, value, step]);

    if (readonly) {
        return (
            <div
                className={classNames(styles.block, className, styles.readonly)}
            >
                <Text.Bold14 className={styles.count}>{value} шт.</Text.Bold14>
            </div>
        );
    }

    return (
        <div className={classNames(styles.block, className)}>
            <button
                className={classNames(styles.buttonMinus, {
                    [styles.disabled]: value <= minValue,
                })}
                onClick={handleSubstract}
            >
                <Icon name="minus" className={classNames(styles.icon)} />
            </button>
            {editable && (
                <input
                    className={styles.input}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                />
            )}
            {!editable && (
                <Text.Bold14 className={styles.count}>{value}</Text.Bold14>
            )}
            <button
                className={classNames(styles.buttonPlus, {
                    [styles.disabled]: value >= maxValue,
                })}
                onClick={handleAdd}
            >
                <Icon name="plus12" className={styles.icon} />
            </button>
        </div>
    );
};
